import { useEffect, useRef, useState } from "react";
import { LiaSearchPlusSolid, LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { customerAPI, supplierDieAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Pagination from "../../common/Pagination";
import Model from "../../common/Model";
import FormFields from "../../common/FormFields";
import ConfirmDeleteModal from "../../common/DeleteModel";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import Button from "../../common/Button";
import { RegistryHeader } from "../../common/Shared";

const SupplierDie = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    Supplier_Id: 0,
    Supplier_Name: "",
    Contact_No: "",
    Contact_Person_Name: "",
    GST_No: "",
    Supplier_Address: "",
    created_By: Number(userId),
  });
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [supplierData, setSupplierData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const fetchData = async () => {
    if (isFetchingRef.current) return;
    setLoading(true);

    try {
      const res = await supplierDieAPI.getAll(currentPage, pageSize);
      const newItems = res.data;

      if (currentPage === 1) {
        setSupplierData(newItems);
      } else {
        setSupplierData((prev) => [...prev, ...newItems]);
      }

      if (newItems.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      notify("Error fetching die status data", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(supplierData);
    } else {
      const filtered = supplierData.filter((d) =>
        d.Supplier_Name?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredData(filtered);
    }
  }, [searchQuery, supplierData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setIsModelOpen(true);
    setIsEditing(false);
    setFormData({
      Supplier_Id: 0,
      Supplier_Name: "",
      Contact_No: "",
      Contact_Person_Name: "",
      GST_No: "",
      Supplier_Address: "",
      created_By: Number(userId),
    });
  };

  const handleEdit = async (id) => {
    try {
      const data = await supplierDieAPI.getById(id);
      if (data.data) {
        setFormData({
          Supplier_Id: data.data.supplier_Id,
          Supplier_Name: data.data.supplier_Name,
          Contact_No: data.data.contact_No,
          Contact_Person_Name: data.data.contact_Person_Name,
          GST_No: data.data.gsT_No,
          Supplier_Address: data.data.supplier_Address,
          Modify_By: 1,
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (error) {
      notify("Failed to fetch supplier details", "error");
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await supplierDieAPI.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchData();
    } catch (error) {
      notify("Failed to delete record", "error");
    }
    setIsDeleteModalOpen(false);
    setSelectedId(null);
  };

  const handleSubmit = async ({ resetOnly = false }) => {
    try {
      await supplierDieAPI.saveOrUpdate(formData);
      notify(
        isEditing ? "Updated successfully" : "Added successfully",
        "success",
      );
      fetchData();
      setFormData({
        Supplier_Id: 0,
        Supplier_Name: "",
        Contact_No: "",
        Contact_Person_Name: "",
        GST_No: "",
        Supplier_Address: "",
        created_By: Number(userId),
      });
      setIsEditing(false);
      if (!resetOnly) {
        setIsModelOpen(false);
      }
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    }
  };

  const handleFetchGST = async () => {
    if (!formData.GST_No) return notify("Enter GST Number", "error");

    try {
      const res = await customerAPI.fetchGstNo(formData.GST_No.trim());
      const data = res?.data;

      if (!data) {
        notify("Invalid GST / No Data Found", "error");
        return;
      }

      const gst = data.gstin || formData.GST_No;
      const name = data.trade_name || data.legal_name || formData.Supplier_Name;

      const addrObj = data.place_of_business_principal?.address;

      let fullAddress = "";
      if (addrObj) {
        fullAddress = [
          addrObj.door_num,
          addrObj.building_name,
          addrObj.street,
          addrObj.location,
          addrObj.state,
          addrObj.pin_code,
        ]
          .filter(Boolean)
          .join(", ");
      }

      setFormData((prev) => ({
        ...prev,
        GST_No: gst,
        Supplier_Name: name,
        Supplier_Address: fullAddress || prev.Supplier_Address,
      }));

      notify("GST Details Fetched!", "success");
    } catch (err) {
      notify("Invalid GST / Failed Fetch", "error");
    }
  };

  const fields = [
    {
      name: "GST_No",
      label: "GST No",
      type: "text",
      fetchable: true,
      onFetch: handleFetchGST,
    },
    {
      name: "Supplier_Name",
      label: "Supplier Name",
      type: "text",
      required: true,
    },
    { name: "Contact_No", label: "Contact No", type: "number" },
    { name: "Contact_Person_Name", label: "Contact Person Name", type: "text" },

    {
      name: "Supplier_Address",
      label: "Supplier Address",
      type: "text",
    },
  ];

  return (
    <>
      <div className="flex flex-col h-full bg-white font-plus">
        <RegistryHeader title="Supplier Master">
          <div className="relative w-full sm:w-80 group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-primary">
              <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search by SUPPLIER NAME..."
              className="block w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-primary focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
              >
                <MdClose size={18} />
              </button>
            )}
          </div>

          <button
            onClick={handleAdd}
            className="bg-primary text-white font-black text-[10px] uppercase tracking-[3px] px-8 py-3 border border-primary hover:bg-[#2c5ebc] transition-all flex items-center gap-3"
          >
            <HiOutlinePlus size={16} /> Add SUPPLIER
          </button>
        </RegistryHeader>

        <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col">
          <div
            ref={tableScrollRef}
            className="relative overflow-auto border border-gray-200 bg-white flex-1"
          >
            <div className="min-w-max">
              {loading && <Loader centered />}
              <Table
                columns={[
                  { key: "supplier_Name", label: "Supplier Name" },
                  { key: "contact_Person_Name", label: "Contact Person" },
                  { key: "contact_No", label: "Contact No" },
                  { key: "gsT_No", label: "GST No" },
                  { key: "supplier_Address", label: "Address" },
                ]}
                data={filteredData}
                onEdit={(row) => handleEdit(row.supplier_Id)}
                onDelete={(row) => handleDelete(row.supplier_Id)}
                tableClassName="text-[12px] font-bold"
              />
            </div>
          </div>
        </div>
      </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Supplier" : "Add Supplier"}
      >
        <FormFields
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: false });
          }}
          onSubmitAndAddNew={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: true });
          }}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={fields}
          isEditing={isEditing}
          onClear={() => setIsModelOpen(false)}
        />
      </Model>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </>
  );
};

export default SupplierDie;
